import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form-mui';

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';

import * as amplitude from '@amplitude/analytics-browser';

import { getTSPByKey } from '../../tsps';
import { OAuthLoginProps } from '../../types/LoginFormData';
import { loginComponentStyles } from '../ComponentStyles';
import { OnboardGridItem } from '../OnboardGridItem';
import LoginCreds from './LoginCreds';

// Generic component that wraps the specific provider component
interface OAuthLoginWrapperProps {
  providerKey: string;
  onValidityChange: (isValid: boolean) => void;
  tsp: string;
  serviceName?: string;
  onDirectConnect: (providerKey: string) => void;
  showCredentialsOption?: boolean; // Whether to show username/password option
}

const OAuthLoginWrapper: React.FC<OAuthLoginWrapperProps> = ({
  providerKey,
  onDirectConnect,
  onValidityChange,
  tsp,
  serviceName = '',
  showCredentialsOption = true,
}) => {
  const provider = getTSPByKey(providerKey);
  const [skipCredentials, setSkipCredentials] = useState(false);
  const [hasUsername, setHasUsername] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  const { setValue } = useFormContext();

  // Initialize form validity based on options
  useEffect(() => {
    if (!showCredentialsOption) {
      // If not showing credentials option, form is always valid
      onValidityChange(true);
    } else {
      // When showing credentials option, form is only valid if
      // either skipCredentials is true OR both username and password are filled
      const isValid = skipCredentials || (hasUsername && hasPassword);
      onValidityChange(isValid);
    }
  }, [
    onValidityChange,
    showCredentialsOption,
    skipCredentials,
    hasUsername,
    hasPassword,
  ]);

  const handleConnect = () => {
    amplitude.track('OAuth Connect Button Clicked', {
      tsp: providerKey,
      serviceName: serviceName || '',
    });

    onDirectConnect(providerKey);
  };

  // Handle skip credentials checkbox
  const handleSkipCredentials = () => {
    const newSkipValue = !skipCredentials;

    if (newSkipValue) {
      amplitude.track('User Selected Skip Credentials', {
        tsp: providerKey,
        serviceName,
      });

      // Update form state
      setValue('skipCredentials', true);
      setValue('username', '');
      setValue('password', '');

      // Update local state
      setHasUsername(false);
      setHasPassword(false);
      setSkipCredentials(true);
      onValidityChange(true); // Form is valid when skipping credentials
    } else {
      setValue('skipCredentials', false);
      setSkipCredentials(false);
      onValidityChange(hasUsername && hasPassword); // Form is valid only if both fields are filled
    }
  };

  // Handle field changes for username/password
  const handleFieldChange = (field: 'username' | 'password', value: string) => {
    const newHasUsername = field === 'username' ? !!value : hasUsername;
    const newHasPassword = field === 'password' ? !!value : hasPassword;

    if (field === 'username') {
      setHasUsername(!!value);
    } else {
      setHasPassword(!!value);
    }

    // Update form validity - valid if skipping credentials OR both fields are filled
    const isValid = skipCredentials || (newHasUsername && newHasPassword);
    onValidityChange(isValid);
  };

  if (!provider) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color='error'>
          Provider configuration not found for {providerKey}
        </Typography>
      </Box>
    );
  }

  // Simple OAuth-only view
  if (!showCredentialsOption) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 2,
        }}
      >
        <Typography variant='body1' align='center' sx={{ mb: 3 }}>
          Connect your {provider.name} account using secure OAuth authentication
        </Typography>

        <Button
          onClick={handleConnect}
          variant='contained'
          color='primary'
          size='large'
          sx={{ minWidth: '200px' }}
        >
          Connect with {provider.name}
        </Button>

        <Typography
          variant='body2'
          color='textSecondary'
          align='center'
          sx={{ mt: 3 }}
        >
          You'll be redirected to {provider.name} to securely authorize access
        </Typography>
      </Box>
    );
  }

  // Full view with username/password option
  return (
    <Box sx={loginComponentStyles.container}>
      {/* Username/Password login fields */}
      <OnboardGridItem>
        <LoginCreds
          disabled={skipCredentials}
          onFieldChange={{
            username: (value) => handleFieldChange('username', value),
            password: (value) => handleFieldChange('password', value),
          }}
        />
      </OnboardGridItem>

      {/* Explanatory text */}
      <OnboardGridItem>
        <Typography variant='body2' sx={loginComponentStyles.explanatoryText}>
          For your convenience, we can securely store your {provider.name}{' '}
          credentials. This way, you won't have to re-enter them unless
          something changes, like a new password or a {provider.name} update. If
          you prefer not to save them, you can always re-authorize when needed.
        </Typography>
      </OnboardGridItem>

      {/* Skip credentials checkbox */}
      <OnboardGridItem>
        <Box sx={loginComponentStyles.checkboxContainer}>
          <FormControlLabel
            control={
              <Checkbox
                checked={skipCredentials}
                onChange={handleSkipCredentials}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                sx={loginComponentStyles.checkbox}
              />
            }
            label="No thanks, I'll re-authorize when needed."
            sx={loginComponentStyles.formControlLabel}
          />
        </Box>
      </OnboardGridItem>
    </Box>
  );
};

// Exported OAuthLogin component that complies with the OAuthLoginProps interface
const OAuthLogin: React.FC<
  OAuthLoginProps & { showCredentialsOption?: boolean }
> = ({
  onDirectConnect,
  tsp,
  serviceName,
  onValidityChange,
  showCredentialsOption = true,
}) => {
  // Use the provider key from tsp prop
  const handleWrappedDirectConnect = (providerKey: string) => {
    // Call the provided onDirectConnect with no parameters as expected
    onDirectConnect();
  };

  return (
    <OAuthLoginWrapper
      providerKey={tsp.toLowerCase()}
      onDirectConnect={handleWrappedDirectConnect}
      tsp={tsp}
      serviceName={serviceName}
      onValidityChange={onValidityChange}
      showCredentialsOption={showCredentialsOption}
    />
  );
};

export default OAuthLogin;
